import React from 'react';
import { observer } from 'mobx-react-lite';
import { useDroppable } from '@dnd-kit/core';
import { TableOptions, useReactTable } from '@tanstack/react-table';

import { DayStartStrip } from './day-start-strip';
import { ShotStrip } from './shot-strip';
import { timeTo24HourFormat, getRenderedTime } from '../../../lib/utils/Convertor';
import { openModal } from '../../../core/modal/open-modal';
import { DayBreakStrip } from './day-break-strip';
import { minutesFormatter } from '../../../lib/time-formatters';
import { BannerStrip } from './banner-strip';
import { RenameStepModal } from '../../process/components/rename-step.modal';
import { EmptyTablePlaceholder } from '../../../components/table/empty-table-placeholder';
import styles from '../stripboard.module.scss';

import type { ShootingDay, DayBreakStripData, Stripboard } from '../../../app/entities/';

type StripboardProps = {
  stripboard: Stripboard;
  // TODO: Remove props that can be caught from stripboard
  shootingDay: ShootingDay;
  totalShootingDays: number;
  tableOptions: Omit<TableOptions<any>, 'data'>;
  totalSize?: number | string;
  projectId?: string;
  onAddShot?: () => void;
  onUpdateStrip?: (
    stripId: string,
    update: {
      data?: {
        text?: string;
        estimatedTime?: number;
        isHidden?: boolean;
      };
    },
  ) => Promise<void>;
};

export const ShootingDayTable: React.FC<StripboardProps> = observer(
  ({
    stripboard,
    shootingDay,
    totalShootingDays,
    tableOptions,
    onAddShot,
    totalSize,
    projectId,
    onUpdateStrip,
  }) => {
    const table = useReactTable({
      data: shootingDay.strips,
      ...tableOptions,
    });

    const rows = table.getRowModel().rows;

    const { setNodeRef, isOver, active } = useDroppable({
      id: shootingDay._id,
      data: {
        type: 'shooting-day',
        accepts: ['row'],
        daybreakId: shootingDay.daybreakId,
      },
    });

    const handleChangeTime = async (value: { hour: number; minute: number }) => {
      const time = timeTo24HourFormat(value);
      const { hour, minute } = time || {};

      await shootingDay.update({
        info: {
          generalCallTime: {
            hour,
            minute,
          },
        },
      });
    };

    const openRenameModal = () =>
      openModal(RenameStepModal, {
        step: shootingDay,
      });

    return (
      <div
        style={{
          width: `max(${totalSize}px, 100%)`,
        }}
        className={styles.singleTable}
        ref={setNodeRef}
      >
        <DayStartStrip
          dayName={`(#${shootingDay.dod}) ${shootingDay.name}`}
          dodText={`Day ${shootingDay.dod} of ${totalShootingDays}`}
          startDate={shootingDay.startDate}
          generalCallTime={shootingDay.info?.generalCallTime}
          onChangeTime={handleChangeTime}
          onRenameClick={openRenameModal}
          onAddShot={onAddShot}
          dayId={shootingDay._id}
        />

        <div className={styles['table-body']}>
          {rows?.length === 1 && (
            <div className={styles.stickyStrip} data-variant="empty-placeholder">
              {active?.data?.current?.type === 'row' && isOver && (
                <div className={styles.stickyStripContent}>
                  <div className={styles['drop-target']} />
                </div>
              )}

              <EmptyTablePlaceholder description="There are no shots scheduled for this day." />
            </div>
          )}

          {rows.map((row) => {
            const strip = row.original;

            if (strip?.type === 'dayBreak') {
              const { dod, endTime, totalEstimatedTime } = (strip.data || {}) as DayBreakStripData;

              const renderedTime: string | undefined = endTime
                ? getRenderedTime(endTime)
                : undefined;

              return (
                <DayBreakStrip
                  key={strip?._id}
                  centerText={
                    renderedTime ? `End of day ${dod} at ${renderedTime}` : `End of day ${dod}`
                  }
                  endText={
                    <p>
                      Total time:{' '}
                      <span className="bold">{minutesFormatter(totalEstimatedTime)}</span>
                    </p>
                  }
                  dayId={shootingDay?._id}
                  row={row}
                />
              );
            }

            if (strip?.type === 'banner') {
              const handleUpdate = async (text?: string) => {
                onUpdateStrip?.(strip._id, { data: { text } });
              };

              return (
                <BannerStrip
                  key={strip?._id}
                  row={row}
                  projectId={projectId}
                  onUpdateText={handleUpdate}
                  dayId={shootingDay?._id}
                />
              );
            }

            return (
              <ShotStrip
                key={strip?._id}
                row={row}
                dayId={shootingDay?._id}
                stripboard={stripboard}
              />
            );
          })}
        </div>
      </div>
    );
  },
);
